import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, RefreshCw, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { PersonStatusBadge, PersonTypeBadge } from '../components/badges';
import { PersonFormModal } from '../components/PersonFormModal';
import { Badge, Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Tabs, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatRelative, initials, personName } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import { gradeLabel, useRosterSchools } from '../lib/oneroster';
import type { Page, PersonListItem } from '../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = { type: '', search: '', status: 'ACTIVE', gradYear: '', source: '', schoolId: '', hasAssets: '', page: '1', sort: 'name', dir: 'asc' };

const SOURCE_LABEL: Record<string, string> = { BLACKBAUD: 'Blackbaud', ONEROSTER: 'SIS', IMPORT: 'Imported', MANUAL: 'Manual' };
const TYPE_TABS = [
  { value: '', label: 'Everyone' },
  { value: 'STUDENT', label: 'Students' },
  { value: 'TEACHER', label: 'Teachers' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'CANDIDATE', label: 'Candidates' },
  { value: 'ALUMNI', label: 'Alumni' },
  { value: 'PAST_STUDENT', label: 'Past students' },
];

export function PeoplePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const [adding, setAdding] = useState(false);
  const schools = useRosterSchools(can('TECHNICIAN'));
  const activeSchools = (schools.data ?? []).filter((school) => school.active);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const query = useQuery({
    queryKey: ['people', 'list', filters],
    queryFn: () => api.get<Page<PersonListItem>>('/people', { ...filters, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const thisYear = new Date().getFullYear();
  const gradYears = Array.from({ length: 22 }, (_, i) => thisYear + 8 - i);

  return (
    <>
      <PageHeader
        title="People"
        description="Students, faculty, staff, alumni, and admissions candidates."
        actions={
          <>
            {can('TECHNICIAN') && (
              <Link to={activeSchools.length ? '/integrations/oneroster' : '/integrations/blackbaud'}>
                <Button icon={<RefreshCw />}>{activeSchools.length ? 'SIS sync' : 'Blackbaud sync'}</Button>
              </Link>
            )}
            {can('TECHNICIAN') && (
              <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                Add person
              </Button>
            )}
          </>
        }
      />

      <Tabs className="mb-4" value={filters.type} onChange={(type) => setFilters({ type, gradYear: '' })} tabs={TYPE_TABS} />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email, or ID" className="w-full sm:w-72" />
          <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="">Active and inactive</option>
          </Select>
          {(filters.type === '' || filters.type === 'STUDENT' || filters.type === 'ALUMNI' || filters.type === 'PAST_STUDENT') && (
            <Select value={filters.gradYear} onChange={(e) => setFilters({ gradYear: e.target.value })} className="w-auto">
              <option value="">Any grad year</option>
              {gradYears.map((year) => (
                <option key={year} value={year}>
                  Class of {year}
                </option>
              ))}
            </Select>
          )}
          {activeSchools.length > 0 && (
            <Select value={filters.schoolId} onChange={(e) => setFilters({ schoolId: e.target.value })} className="w-auto max-w-full" aria-label="School">
              <option value="">Any school</option>
              {activeSchools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </Select>
          )}
          <Select value={filters.source} onChange={(e) => setFilters({ source: e.target.value })} className="w-auto">
            <option value="">Any source</option>
            <option value="ONEROSTER">SIS (OneRoster)</option>
            <option value="BLACKBAUD">Blackbaud</option>
            <option value="MANUAL">Added manually</option>
          </Select>
          <Select value={filters.hasAssets} onChange={(e) => setFilters({ hasAssets: e.target.value })} className="w-auto">
            <option value="">With or without assets</option>
            <option value="true">Has assets checked out</option>
            <option value="false">No assets checked out</option>
          </Select>
          {query.isFetching && !query.isLoading && <Spinner className="size-4" />}
        </div>

        {query.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data!.items.length === 0 ? (
          <EmptyState icon={<Users />} title="No people found" description="Adjust the filters, or connect your SIS or Blackbaud to sync your roster." />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Class / grade</Th>
                  <Th>ID</Th>
                  <Th>Status</Th>
                  <Th>Assets</Th>
                  <Th>Source</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {query.data!.items.map((person) => (
                  <tr key={person.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/people/${person.id}`)}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
                          {initials(personName(person))}
                        </div>
                        <div className="min-w-0">
                          <Link to={`/people/${person.id}`} onClick={(e) => e.stopPropagation()} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                            {person.lastName}, {person.preferredName || person.firstName}
                          </Link>
                          <p className="truncate text-xs text-slate-500">{person.email ?? 'No email'}</p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <PersonTypeBadge type={person.type} />
                    </Td>
                    <Td className="whitespace-nowrap">
                      {person.gradYear ? `Class of ${person.gradYear}` : person.gradeLevel ? '' : '—'}
                      {person.gradeLevel && <p className={person.gradYear ? 'text-xs text-slate-500' : undefined}>{gradeLabel(person.gradeLevel)}</p>}
                      {person.rosterSchool && <p className="max-w-[12rem] truncate text-xs text-slate-500">{person.rosterSchool.name}</p>}
                    </Td>
                    <Td className="font-mono text-xs text-slate-600">{person.hostId ?? '—'}</Td>
                    <Td>
                      <PersonStatusBadge status={person.status} />
                    </Td>
                    <Td>{person.activeCheckoutCount > 0 ? <Badge tone="blue">{person.activeCheckoutCount}</Badge> : <span className="text-slate-400">—</span>}</Td>
                    <Td className="text-xs whitespace-nowrap text-slate-500">
                      {person.source === 'BLACKBAUD' || person.source === 'ONEROSTER' ? (
                        <span title={person.lastSyncedAt ? `Last synced ${formatRelative(person.lastSyncedAt)}` : undefined}>{SOURCE_LABEL[person.source]}</span>
                      ) : (
                        SOURCE_LABEL[person.source]
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={query.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <PersonFormModal open={adding} onClose={() => setAdding(false)} onSaved={(person) => navigate(`/people/${person.id}`)} />
    </>
  );
}
